import { osnovaApi } from '../base';
import type { UploadBatchResponse } from '../types';
import type { CacheStats, ChatMessage, ChatScope, ChatSession } from '@entities/chat/types';
import type { ComplianceCheck, ComplianceIssue, Severity } from '@entities/compliance/types';
import type { ChecklistRule, KnowledgeChunk, KnowledgeDocument } from '@entities/knowledge/types';
import type { TrashCount, TrashEntityType, TrashItem } from '@entities/legal/types';

export const kbApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    listKnowledge: b.query<{ items: KnowledgeDocument[] }, { document_type?: string } | void>({
      query: (p) => ({
        url: '/agents/statut/knowledge',
        params: p ?? undefined,
      }),
      providesTags: ['Knowledge'],
    }),
    getDocument: b.query<KnowledgeDocument, string>({
      query: (id) => `/agents/statut/knowledge/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Knowledge', id }],
    }),
    listChunks: b.query<
      { items: KnowledgeChunk[] },
      { docId: string; limit?: number; offset?: number }
    >({
      query: ({ docId, ...params }) => ({
        url: `/agents/statut/knowledge/${docId}/chunks`,
        params,
      }),
    }),
    uploadKnowledge: b.mutation<UploadBatchResponse, FormData>({
      query: (form) => ({
        url: '/agents/statut/knowledge/upload',
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['Knowledge', 'CacheStats'],
    }),
    // Сделать шаблон/чеклист активным для своего типа (один включённый
    // на тип). Инвалидируем весь список — сервер сам гасит прежний.
    toggleDocument: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/knowledge/${id}/toggle`,
        method: 'POST',
      }),
      invalidatesTags: ['Knowledge'],
    }),
    // Извлечённые агентом правила чек-листа (document_type='checklist').
    listDocumentRules: b.query<{ items: ChecklistRule[] }, string>({
      query: (id) => `/agents/statut/knowledge/${id}/rules`,
      providesTags: (_r, _e, id) => [{ type: 'Knowledge', id }],
    }),

    // ─── Корзина: единый список мягко удалённых статут-сущностей ──────────
    // Все типы (knowledge/contract/claim/lawsuit/lna) собраны в один
    // источник. Восстановление возвращает запись в её родной реестр —
    // поэтому инвалидируем и тег «Корзины», и тег списка сущности.
    listTrash: b.query<
      { items: TrashItem[] },
      { entity_type?: TrashEntityType; search?: string; limit?: number; offset?: number } | void
    >({
      query: (p) => ({
        url: '/agents/statut/trash',
        params: p ?? undefined,
      }),
      providesTags: ['StatutTrash'],
    }),
    trashCounts: b.query<{ counts: TrashCount[] }, void>({
      query: () => '/agents/statut/trash/counts',
      providesTags: ['StatutTrash'],
    }),
    restoreTrash: b.mutation<{ ok: boolean }, { entity_type: TrashEntityType; id: string }>({
      query: ({ entity_type, id }) => ({
        url: `/agents/statut/trash/${entity_type}/${id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { entity_type }) => {
        // entity_type → тег списка соответствующего реестра.
        const listTag = {
          knowledge: 'Knowledge',
          contract: 'Contract',
          claim: 'Claim',
          lawsuit: 'Lawsuit',
          lna: 'Lna',
        } as const;
        return ['StatutTrash', listTag[entity_type]];
      },
    }),
    archiveDocument: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/knowledge/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Knowledge', 'CacheStats'],
      // Optimistic: drop the row from every cached list variant immediately
      // so the UI feels instantaneous; rollback if the server rejects.
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        // Patch each known cached variant of listKnowledge.
        const variants: (undefined | { document_type: string })[] = [
          undefined,
          { document_type: 'gost' },
          { document_type: 'snip' },
          { document_type: 'sp' },
          { document_type: 'tu' },
          { document_type: 'internal' },
        ];
        const patches = variants.map((arg) =>
          dispatch(
            kbApi.util.updateQueryData('listKnowledge', arg, (draft) => {
              draft.items = draft.items.filter((d) => d.id !== id);
            }),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    startCheck: b.mutation<{ check_id: string; status: string }, FormData>({
      query: (form) => ({
        url: '/agents/statut/check',
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['Check'],
    }),
    getCheck: b.query<ComplianceCheck, string>({
      query: (id) => `/agents/statut/check/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Check', id }],
    }),
    listIssues: b.query<
      { items: ComplianceIssue[] },
      { checkId: string; severity?: Severity; limit?: number; offset?: number }
    >({
      query: ({ checkId, ...params }) => ({
        url: `/agents/statut/check/${checkId}/issues`,
        params,
      }),
      providesTags: (_r, _e, { checkId }) => [{ type: 'Issues', id: checkId }],
    }),
    listChecks: b.query<{ items: ComplianceCheck[] }, void>({
      query: () => '/agents/statut/checks',
      providesTags: ['Check'],
    }),
    deleteCheck: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/check/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Check'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          kbApi.util.updateQueryData('listChecks', undefined, (draft) => {
            draft.items = draft.items.filter((c) => c.id !== id);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // ===== Chat =====
    listChatSessions: b.query<
      { items: ChatSession[] },
      {
        scope?: ChatScope;
        compliance_check_id?: string;
        scope_id?: string;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/chat/sessions',
        params: p ?? undefined,
      }),
      providesTags: ['ChatSession'],
    }),
    startChat: b.mutation<
      ChatSession,
      {
        scope: ChatScope;
        compliance_check_id?: string;
        /** contract_id для scope=contract, claim_id для scope=claim и т.д. */
        scope_id?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/statut/chat/sessions',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ChatSession'],
    }),
    getChatSession: b.query<{ session: ChatSession; messages: ChatMessage[] }, string>({
      query: (id) => `/agents/statut/chat/sessions/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ChatMessages', id }],
    }),
    sendChatMessage: b.mutation<{ message: ChatMessage }, { sessionId: string; content: string }>({
      query: ({ sessionId, content }) => ({
        url: `/agents/statut/chat/sessions/${sessionId}/messages`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: 'ChatMessages', id: sessionId },
        'ChatSession',
        'CacheStats',
      ],
    }),
    renameChatSession: b.mutation<ChatSession, { sessionId: string; title: string }>({
      query: ({ sessionId, title }) => ({
        url: `/agents/statut/chat/sessions/${sessionId}`,
        method: 'PATCH',
        body: { title },
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        'ChatSession',
        { type: 'ChatMessages', id: sessionId },
      ],
      async onQueryStarted({ sessionId, title }, { dispatch, queryFulfilled, getState }) {
        const patches: { undo: () => void }[] = [];
        patches.push(
          dispatch(
            kbApi.util.updateQueryData('getChatSession', sessionId, (draft) => {
              if (draft.session) draft.session.title = title;
            }),
          ),
        );
        // Find every cached variant of listChatSessions and patch them all.
        const queries = (getState() as { osnovaApi: { queries: Record<string, unknown> } })
          .osnovaApi.queries;
        for (const key of Object.keys(queries)) {
          if (!key.startsWith('listChatSessions(')) continue;
          const rawArg = key.slice('listChatSessions('.length, -1);
          let arg: unknown;
          try {
            arg = rawArg === 'undefined' ? undefined : JSON.parse(rawArg);
          } catch {
            continue;
          }
          patches.push(
            dispatch(
              kbApi.util.updateQueryData('listChatSessions', arg as never, (draft) => {
                const s = draft.items.find((x) => x.id === sessionId);
                if (s) s.title = title;
              }),
            ),
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),
    deleteChatSession: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/chat/sessions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ChatSession'],
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const queries = (getState() as { osnovaApi: { queries: Record<string, unknown> } })
          .osnovaApi.queries;
        const patches: { undo: () => void }[] = [];
        for (const key of Object.keys(queries)) {
          if (!key.startsWith('listChatSessions(')) continue;
          const rawArg = key.slice('listChatSessions('.length, -1);
          let arg: unknown;
          try {
            arg = rawArg === 'undefined' ? undefined : JSON.parse(rawArg);
          } catch {
            continue;
          }
          patches.push(
            dispatch(
              kbApi.util.updateQueryData('listChatSessions', arg as never, (draft) => {
                draft.items = draft.items.filter((s) => s.id !== id);
              }),
            ),
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    cacheStats: b.query<CacheStats, void>({
      query: () => '/agents/statut/cache/stats',
      providesTags: ['CacheStats'],
    }),

  }),
  overrideExisting: false,
});

export const {
  useListKnowledgeQuery,
  useGetDocumentQuery,
  useListChunksQuery,
  useUploadKnowledgeMutation,
  useArchiveDocumentMutation,
  useToggleDocumentMutation,
  useListDocumentRulesQuery,
  useListTrashQuery,
  useTrashCountsQuery,
  useRestoreTrashMutation,
  useStartCheckMutation,
  useGetCheckQuery,
  useListIssuesQuery,
  useListChecksQuery,
  useDeleteCheckMutation,
  useListChatSessionsQuery,
  useLazyListChatSessionsQuery,
  useStartChatMutation,
  useGetChatSessionQuery,
  useSendChatMessageMutation,
  useRenameChatSessionMutation,
  useDeleteChatSessionMutation,
  useCacheStatsQuery,
} = kbApi;
